<template>
	<div v-show="show" class="right-menu-mask" @click.stop="close()" @contextmenu.prevent="close()">
		<div
			ref="rootMenu"
			class="right-menu"
			:style="{ 'left': pos.x + 'px', 'top': pos.y + 'px' }"
			@mouseleave="onRootMenuMouseLeave"
		>
			<div class="menu-container">
				<div
					v-for="(item) in items"
					:key="item.key"
					class="menu-item"
					:class="{
						'danger': item.danger,
						'disabled': isItemDisabled(item),
						'has-children': hasChildren(item),
						'submenu-active': activeChildKey === item.key
					}"
					@mouseenter="onItemMouseEnter(item, $event)"
					@click.stop="onSelectMenu(item)"
				>
					<span class="menu-icon-shell">
						<i v-if="item.icon" :class="item.icon" class="menu-icon"></i>
					</span>
					<span class="menu-text">{{ item.name }}</span>
					<i v-if="hasChildren(item)" class="el-icon-arrow-right menu-arrow"></i>
				</div>
			</div>
		</div>
		<div
			v-if="childItems.length > 0"
			ref="submenu"
			class="right-menu right-submenu"
			:style="{ 'left': childPos.x + 'px', 'top': childPos.y + 'px' }"
			@mouseleave="onSubmenuMouseLeave"
		>
			<div class="menu-container">
				<div
					v-for="item in childItems"
					:key="item.key"
					class="menu-item"
					:class="{ 'danger': item.danger, 'disabled': isItemDisabled(item) }"
					@click.stop="onSelectMenu(item)"
				>
					<span class="menu-icon-shell">
						<i v-if="item.icon" :class="item.icon" class="menu-icon"></i>
					</span>
					<span class="menu-text">{{ item.name }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { dismissStack, resolveZIndex } from '../../utils/dismissStack';

export default {
	name: "rightMenu",
	data() {
		return {
			disposeDismiss: null,
			viewportGap: 8,
			cursorGap: 2,
			anchorGap: 4,
			show: false,
			pos: {
				x: 0,
				y: 0,
			},
			childPos: {
				x: 0,
				y: 0
			},
			anchor: {
				kind: 'point',
				x: 0,
				y: 0,
				left: 0,
				top: 0,
				right: 0,
				bottom: 0
			},
			items: [],
			activeChildKey: '',
			childItems: [],
			childAnchorRect: null
		}
	},
	mounted() {
		if (this.$el && this.$el.parentNode !== document.body) {
			document.body.appendChild(this.$el);
		}
		this.disposeDismiss = dismissStack.register({
			id: `right-menu-${this._uid}`,
			isOpen: () => this.show,
			getZIndex: () => resolveZIndex([
				this.$el,
				this.$el ? this.$el.querySelector('.right-menu') : null
			], 10000),
			close: () => this.close()
		});
		window.addEventListener('resize', this.onViewportChange);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.onViewportChange);
		if (this.disposeDismiss) {
			this.disposeDismiss();
			this.disposeDismiss = null;
		}
		if (this.$el && this.$el.parentNode) {
			this.$el.parentNode.removeChild(this.$el);
		}
	},
	methods: {
		open(pos, items) {
			this.anchor = this.resolveAnchor(pos);
			this.pos.x = this.anchor.left;
			this.pos.y = this.anchor.top;
			this.items = Array.isArray(items) ? items : [];
			this.hideSubmenu();
			this.show = true;
			this.$nextTick(() => {
				this.rejustPos();
			});
		},
		close() {
			this.show = false;
			this.hideSubmenu();
		},
		onSelectMenu(item) {
			if (this.isItemDisabled(item)) {
				return;
			}
			if (this.hasChildren(item)) {
				return;
			}
			this.$emit("select", item);
			this.close();
		},
		onViewportChange() {
			if (!this.show) {
				return;
			}
			this.rejustPos();
			if (this.childItems.length > 0) {
				this.$nextTick(() => {
					this.rejustSubmenu();
				});
			}
		},
		hasChildren(item) {
			return Array.isArray(item && item.children) && item.children.length > 0;
		},
		isItemDisabled(item) {
			return Boolean(item && item.disabled);
		},
		onItemMouseEnter(item, event) {
			if (this.hasChildren(item) && !this.isItemDisabled(item)) {
				this.openSubmenu(item, event && event.currentTarget);
				return;
			}
			this.hideSubmenu();
		},
		onRootMenuMouseLeave(event) {
			if (this.isRelatedToRef(event, 'submenu')) {
				return;
			}
			this.hideSubmenu();
		},
		onSubmenuMouseLeave(event) {
			if (this.isRelatedToRef(event, 'rootMenu')) {
				return;
			}
			this.hideSubmenu();
		},
		isRelatedToRef(event, refName) {
			const relatedTarget = event && event.relatedTarget;
			const element = this.$refs[refName];
			return Boolean(element && relatedTarget && element.contains(relatedTarget));
		},
		hideSubmenu() {
			this.activeChildKey = '';
			this.childItems = [];
			this.childAnchorRect = null;
		},
		openSubmenu(item, anchorElement) {
			const children = Array.isArray(item && item.children) ? item.children : [];
			if (children.length === 0 || !anchorElement || !anchorElement.getBoundingClientRect) {
				this.hideSubmenu();
				return;
			}
			this.activeChildKey = item.key;
			this.childItems = children;
			this.childAnchorRect = anchorElement.getBoundingClientRect();
			this.$nextTick(() => {
				this.rejustSubmenu();
			});
		},
		resolveAnchor(pos) {
			const x = this.readCoord(pos, ['clientX', 'x', 'pageX']);
			const y = this.readCoord(pos, ['clientY', 'y', 'pageY']);
			const safeX = Number.isFinite(x) ? x : this.viewportGap;
			const safeY = Number.isFinite(y) ? y : this.viewportGap;
			const currentTarget = pos && pos.currentTarget;
			if (currentTarget && pos.type && pos.type !== 'contextmenu' && currentTarget.getBoundingClientRect) {
				const rect = currentTarget.getBoundingClientRect();
				return {
					kind: 'element',
					x: safeX,
					y: safeY,
					left: rect.left,
					top: rect.top,
					right: rect.right,
					bottom: rect.bottom
				};
			}
			return {
				kind: 'point',
				x: safeX,
				y: safeY,
				left: safeX,
				top: safeY,
				right: safeX,
				bottom: safeY
			};
		},
		readCoord(pos, keys) {
			for (const key of keys) {
				const value = pos && pos[key];
				if (Number.isFinite(value)) {
					return value;
				}
			}
			return null;
		},
		resolveMenuPos(menuW, menuH) {
			const anchor = this.anchor || {};
			if (anchor.kind === 'element') {
				let x = anchor.left;
				let y = anchor.bottom + this.anchorGap;
				if (x + menuW > window.innerWidth - this.viewportGap) {
					x = anchor.right - menuW;
				}
				if (y + menuH > window.innerHeight - this.viewportGap) {
					y = anchor.top - menuH - this.anchorGap;
				}
				return { x, y };
			}
			let x = (anchor.x || 0) + this.cursorGap;
			let y = (anchor.y || 0) + this.cursorGap;
			if (x + menuW > window.innerWidth - this.viewportGap) {
				x = (anchor.x || 0) - menuW - this.cursorGap;
			}
			if (y + menuH > window.innerHeight - this.viewportGap) {
				y = (anchor.y || 0) - menuH - this.cursorGap;
			}
			return { x, y };
		},
		resolveSubmenuPos(menuW, menuH) {
			const anchor = this.childAnchorRect;
			if (!anchor) {
				return {
					x: this.pos.x,
					y: this.pos.y
				};
			}
			let x = anchor.right;
			let y = anchor.top;
			if (x + menuW > window.innerWidth - this.viewportGap) {
				x = anchor.left - menuW;
			}
			if (y + menuH > window.innerHeight - this.viewportGap) {
				y = window.innerHeight - menuH - this.viewportGap;
			}
			return { x, y };
		},
		estimateMenuWidth(items) {
			return Math.min(208, Math.max(132, ...items.map((item) => {
				const textLength = String(item && item.name || '').length;
				const childSpace = this.hasChildren(item) ? 18 : 0;
				return 78 + textLength * 14 + childSpace + (item && item.icon ? 18 : 0);
			})));
		},
		estimateMenuHeight(items) {
			return items.length * 40 + 20;
		},
		rejustPos() {
			const estimatedWidth = this.estimateMenuWidth(this.items);
			const panel = this.$refs.rootMenu;
			const panelRect = panel ? panel.getBoundingClientRect() : null;
			const menuH = panelRect ? panelRect.height : this.estimateMenuHeight(this.items);
			const menuW = panelRect ? panelRect.width : estimatedWidth;
			const nextPos = this.resolveMenuPos(menuW, menuH);
			const maxY = window.innerHeight - menuH - this.viewportGap;
			const maxX = window.innerWidth - menuW - this.viewportGap;
			this.pos.y = Math.max(this.viewportGap, Math.min(nextPos.y, maxY));
			this.pos.x = Math.max(this.viewportGap, Math.min(nextPos.x, maxX));
		},
		rejustSubmenu() {
			if (this.childItems.length === 0) {
				return;
			}
			const panel = this.$refs.submenu;
			const panelRect = panel ? panel.getBoundingClientRect() : null;
			const menuW = panelRect ? panelRect.width : this.estimateMenuWidth(this.childItems);
			const menuH = panelRect ? panelRect.height : this.estimateMenuHeight(this.childItems);
			const nextPos = this.resolveSubmenuPos(menuW, menuH);
			const maxY = window.innerHeight - menuH - this.viewportGap;
			const maxX = window.innerWidth - menuW - this.viewportGap;
			this.childPos.x = Math.max(this.viewportGap, Math.min(nextPos.x, maxX));
			this.childPos.y = Math.max(this.viewportGap, Math.min(nextPos.y, maxY));
		}
	}
}
</script>

<style lang="scss">
.right-menu-mask {
	position: fixed;
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	width: 100%;
	height: 100%;
	z-index: 9999;
}

.right-menu {
	position: fixed;
	border-radius: 6px;
	overflow: hidden;
	box-shadow: 0 16px 32px -24px rgba(var(--im-black-rgb), 0.42);
	background: var(--im-surface-panel-strong);
	backdrop-filter: none;
	border: 1px solid var(--im-surface-panel-border);
	z-index: 10000;

	.menu-container {
		padding: 3px;
		min-width: 148px;

		.menu-item {
			display: grid;
			grid-template-columns: 14px minmax(0, 1fr) auto;
			align-items: center;
			column-gap: 6px;
			min-height: 28px;
			padding: 5px 8px;
			border-radius: 4px;
			cursor: pointer;
			transition: color 0.2s ease, background-color 0.2s ease;
			position: relative;
			font-size: 12px;
			color: var(--im-text-color);
			font-weight: 500;

			&:hover {
				background: rgba(var(--im-primary-rgb), 0.08);
				color: var(--im-color-primary);
			}

			&.submenu-active {
				background: rgba(var(--im-primary-rgb), 0.08);
				color: var(--im-color-primary);
			}

			&.disabled {
				cursor: not-allowed;
				color: var(--im-text-color-lighter);

				&:hover {
					background: transparent;
					color: var(--im-text-color-lighter);
				}

				.menu-icon-shell,
				.menu-arrow {
					color: inherit;
				}
			}

			&.danger {
				color: var(--im-color-danger);

				&:hover {
					background: rgba(var(--im-danger-rgb, 245, 108, 108), 0.12);
					color: var(--im-color-danger);
				}

				.menu-icon-shell {
					color: var(--im-color-danger);
				}
			}

			.menu-icon-shell {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				width: 14px;
				height: 14px;
				border-radius: 0;
				background: transparent;
				color: var(--im-icon-color);
				flex-shrink: 0;
				transition: color 0.2s ease;
			}

			&:hover .menu-icon-shell {
				color: inherit;
			}

			.menu-icon {
				font-size: 13px;
				line-height: 1;
			}

			.menu-text {
				min-width: 0;
				line-height: 1.35;
				text-align: left;
			}

			.menu-arrow {
				font-size: 12px;
				line-height: 1;
				color: var(--im-text-color-light);
			}

			&:hover .menu-arrow,
			&.submenu-active .menu-arrow {
				color: inherit;
			}
		}
	}
}

.right-submenu {
	z-index: 10001;
}

@media (max-width: 640px) {
	.right-menu {
		max-width: calc(100vw - 16px);
		border-radius: 6px;
	}
}
</style>
